// src/pages/Events.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  CheckCircle,
  Phone,
  Mail,
  UserRound,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { API_URL, formatDate, getCategoryLabel, getStatusColor } from "../lib/utils";

// -----------------------------------------------------
// Helpers
// -----------------------------------------------------
const fallbackImage =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return fallbackImage;
  const clean = url.trim();
  if (!clean) return fallbackImage;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const toEventDate = (event) => {
  if (!event?.date) return null;
  const d = new Date(event.date);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getDisplayTime = (event) => {
  const start = event?.start_time || "";
  const end = event?.end_time || "";
  const single = event?.time || "";

  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  if (single) return single;
  return "TBD";
};

// -----------------------------------------------------
// Events List
// -----------------------------------------------------
export const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        let url = `${API_URL}/events`;
        const params = [];
        if (statusFilter !== "all") params.push(`status=${encodeURIComponent(statusFilter)}`);
        if (categoryFilter !== "all") params.push(`category=${encodeURIComponent(categoryFilter)}`);
        if (params.length > 0) url += `?${params.join("&")}`;

        const response = await fetch(url);
        const data = await response.json().catch(() => []);
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching events:", error);
        toast.error("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [statusFilter, categoryFilter]);

  const eventDates = useMemo(
    () => events.map((e) => toEventDate(e)).filter(Boolean),
    [events]
  );

  const selectedDayEvents = useMemo(
    () =>
      events.filter((e) => {
        const d = toEventDate(e);
        return d && d.toDateString() === selectedDate.toDateString();
      }),
    [events, selectedDate]
  );

  return (
    <div data-testid="events-page">
<section className="relative overflow-hidden py-20 md:py-32 text-white">
  {/* Background image */}
  <div
    className="absolute inset-0 bg-cover bg-center"
    style={{ backgroundImage: "url('/assets/hero-partner.png')" }}
  />

  {/* Dark overlay */}
  <div className="absolute inset-0 bg-stone-900/70" />

  <div className="relative max-w-7xl mx-auto px-6 md:px-12 z-10">
    <div className="max-w-3xl">
      <p className="font-accent text-xl text-amber-400 mb-4">Events</p>
      <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">Upcoming Events</h1>
      <p className="text-lg text-stone-200">
        Stay informed about upcoming outreaches, campaigns, trainings, and impact activities.
        Join us in making a difference.
      </p>
    </div>
  </div>
</section>


      <section className="py-6 border-b border-stone-200 sticky top-20 bg-white z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="outreach">Outreach</SelectItem>
                  <SelectItem value="campaign">Campaign</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="advocacy">Advocacy</SelectItem>
                  <SelectItem value="community_visit">Community Visit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              {["grid", "calendar", "list"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                    viewMode === mode ? "bg-amber-700 text-white" : "bg-stone-100 text-stone-600"
                  }`}
                  data-testid={`view-${mode}`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-stone-100 rounded-xl h-96 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <>
              {viewMode === "calendar" && (
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <Card>
                      <CardContent className="p-4">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => date && setSelectedDate(date)}
                          modifiers={{ hasEvent: eventDates }}
                          modifiersStyles={{
                            hasEvent: {
                              fontWeight: "bold",
                              backgroundColor: "#FEF3C7",
                              color: "#B45309",
                            },
                          }}
                          className="rounded-md border"
                          data-testid="events-calendar"
                        />
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-2">
                    <h3 className="font-heading text-xl font-bold mb-4">
                      Events on {formatDate(selectedDate.toISOString())}
                    </h3>
                    {selectedDayEvents.length > 0 ? (
                      <div className="space-y-4">
                        {selectedDayEvents.map((event, index) => (
                          <EventCard key={event.id} event={event} index={index} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-stone-500">No events on this date.</p>
                    )}
                  </div>
                </div>
              )}

              {viewMode === "grid" &&
                (events.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.map((event, index) => (
                      <EventCard key={event.id} event={event} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-stone-500">No events found.</p>
                  </div>
                ))}

              {viewMode === "list" &&
                (events.length > 0 ? (
                  <div className="space-y-4">
                    {events.map((event, index) => (
                      <EventListItem key={event.id} event={event} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-stone-500">No events found.</p>
                  </div>
                ))}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const EventCard = ({ event, index }) => {
  return (
    <Card className="card-hover overflow-hidden group" data-testid={`event-card-${index}`}>
      <div className="aspect-video relative overflow-hidden">
        <img
          src={normalizeImageUrl(event.image_url)}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
          <Badge className={getStatusColor(event.status)}>{event.status}</Badge>
          <Badge variant="secondary">{getCategoryLabel(event.category)}</Badge>
        </div>
      </div>

      <CardContent className="p-6">
        <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 mb-3">
          <span className="flex items-center gap-1">
            <CalendarIcon className="w-4 h-4" />
            {formatDate(event.date)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {getDisplayTime(event)}
          </span>
        </div>

        <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">{event.title}</h3>
        <p className="text-stone-600 text-sm mb-4 line-clamp-2">{event.description}</p>

        <div className="flex items-center gap-2 text-sm text-stone-500 mb-1">
          <MapPin className="w-4 h-4" />
          {event.location}
        </div>
        {event.organizer && (
          <div className="flex items-center gap-2 text-sm text-stone-500 mb-4">
            <UserRound className="w-4 h-4" />
            {event.organizer}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Link to={`/events/${event.id}`} className="text-amber-700 font-medium hover:text-amber-800 flex items-center">
            View Details <ArrowRight className="ml-1 w-4 h-4" />
          </Link>

          {event.status === "upcoming" && <RSVPButton eventId={event.id} eventTitle={event.title} />}
        </div>
      </CardContent>
    </Card>
  );
};

const EventListItem = ({ event, index }) => {
  return (
    <Card className="card-hover" data-testid={`event-list-${index}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-full md:w-48 h-32 rounded-lg overflow-hidden flex-shrink-0">
            <img src={normalizeImageUrl(event.image_url)} alt={event.title} className="w-full h-full object-cover" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={getStatusColor(event.status)}>{event.status}</Badge>
              <Badge variant="secondary">{getCategoryLabel(event.category)}</Badge>
            </div>
            <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">{event.title}</h3>

            <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
              <span className="flex items-center gap-1">
                <CalendarIcon className="w-4 h-4" />
                {formatDate(event.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {getDisplayTime(event)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {event.location}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to={`/events/${event.id}`} className="text-amber-700 font-medium hover:text-amber-800">
              Details
            </Link>
            {event.status === "upcoming" && <RSVPButton eventId={event.id} eventTitle={event.title} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RSVPButton = ({ eventId, eventTitle }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/events/${eventId}/rsvp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, event_id: eventId }),
      });

      if (response.ok) {
        toast.success("RSVP successful! See you at the event.");
        setForm({ name: "", email: "", phone: "" });
        setOpen(false);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || "Failed to RSVP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="rsvp-trigger">
          RSVP
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">RSVP for {eventTitle}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="rsvp-form">
          <div className="space-y-2">
            <Label htmlFor="rsvp-name">Full Name *</Label>
            <Input
              id="rsvp-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="rsvp-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rsvp-email">Email Address *</Label>
            <Input
              id="rsvp-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="rsvp-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rsvp-phone">Phone Number</Label>
            <Input
              id="rsvp-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="rsvp-phone"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-700 hover:bg-amber-800"
            data-testid="rsvp-submit"
          >
            {loading ? "Submitting..." : "Confirm RSVP"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_URL}/events/${id}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        } else {
          setEvent(null);
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Event not found</h1>
        <Button asChild>
          <Link to="/events">Back to Events</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="event-detail-page">
      <section className="relative py-20 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${normalizeImageUrl(event.image_url)})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/90"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-white">
          <Link to="/events" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Events
          </Link>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Badge className={getStatusColor(event.status)}>{event.status}</Badge>
            <Badge variant="secondary" className="bg-white/20">
              {getCategoryLabel(event.category)}
            </Badge>
          </div>

          <h1 className="font-heading text-3xl md:text-5xl font-bold mb-6">{event.title}</h1>

          <div className="flex flex-wrap items-center gap-6 text-stone-300">
            <span className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {formatDate(event.date)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {getDisplayTime(event)}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {event.location}
            </span>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-stone-900 mb-4">About This Event</h2>
              <p className="text-stone-600 mb-8">{event.description}</p>

              {event.objectives && event.objectives.length > 0 && (
                <>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">Key Objectives</h3>
                  <ul className="space-y-3 mb-8">
                    {event.objectives.map((obj, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-stone-600">{obj}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {event.target_beneficiaries && (
                <>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">Target Beneficiaries</h3>
                  <p className="text-stone-600 mb-8">{event.target_beneficiaries}</p>
                </>
              )}
            </div>

            <div>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-4">Event Details</h3>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-stone-500">Date</p>
                      <p className="font-medium">{formatDate(event.date)}</p>
                    </div>

                    <div>
                      <p className="text-sm text-stone-500">Time</p>
                      <p className="font-medium">{getDisplayTime(event)}</p>
                    </div>

                    <div>
                      <p className="text-sm text-stone-500">Venue</p>
                      <p className="font-medium">{event.location}</p>
                    </div>

                    <div>
                      <p className="text-sm text-stone-500">Organiser</p>
                      <p className="font-medium">{event.organizer || "GLGIF"}</p>
                    </div>

                    <div>
                      <p className="text-sm text-stone-500">Phone Number</p>
                      <p className="font-medium">{event.phone_number || event.coordinator_contact || "-"}</p>
                    </div>

                    <div>
                      <p className="text-sm text-stone-500">Enquiries Email</p>
                      <p className="font-medium">{event.enquiries_email || "info@glgif.org"}</p>
                    </div>

                    {event.status === "completed" && (
                      <>
                        <div>
                          <p className="text-sm text-stone-500">Lives Touched</p>
                          <p className="font-heading text-2xl font-bold text-amber-700">{event.lives_touched || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-stone-500">Materials Shared</p>
                          <p className="font-heading text-2xl font-bold text-amber-700">
                            {event.materials_shared || 0}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-6 space-y-3">
                    {event.status === "upcoming" && <RSVPButton eventId={event.id} eventTitle={event.title} />}

                    <div className="pt-1 text-sm text-stone-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {event.phone_number || event.coordinator_contact || "-"}
                      </p>
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {event.enquiries_email || "info@glgif.org"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsList;
